import Phaser from "phaser";
import type { BrickCellKind } from "../data/ChapterData.js";
import { PALETTE_NUM } from "../themes/palette.js";
import type { BrickTextureSpec } from "./brickAppearance.js";

export const BRICK_DETAIL_COLOR = 16777215;

const BRICK_DETAIL_ALPHA = 0.45;
const BRICK_DETAIL_FILL_ALPHA = 0.18;
const BRICK_SEGMENT_SHADOW = 0x07191c;
const BRICK_SEGMENT_SHADOW_ALPHA = 0.68;
const BRICK_SEGMENT_HIGHLIGHT_ALPHA = 0.36;
const GOLD_DETAIL_ALPHA = 0.58;
const GOLD_DETAIL_FILL_ALPHA = 0.18;
const MYSTERY_CHEST_ICON_WIDTH_RATIO = 0.76;
const MYSTERY_CHEST_ICON_HEIGHT_RATIO = 0.84;
const MYSTERY_CHEST_ICON_ALPHA = 0.82;

export interface BrickKindDetailSpec {
  readonly kind: BrickCellKind;
  readonly tex: BrickTextureSpec;
  readonly isCircuitVisual: boolean;
  readonly dimGoldVisual: boolean;
  readonly widthCells: number;
  readonly heightCells: number;
  readonly x: number;
  readonly y: number;
  readonly displayWidth: number;
  readonly displayHeight: number;
  readonly kindDetail: Phaser.GameObjects.Graphics;
  readonly mysteryIcon: Phaser.GameObjects.Image;
}

export function drawBrickKindDetail(spec: BrickKindDetailSpec): void {
  const w = spec.displayWidth;
  const h = spec.displayHeight;
  const hw = w / 2;
  const hh = h / 2;
  const pad = Math.max(3, Math.min(w, h) * 0.14);
  const line = Math.max(1, Math.min(w, h) * 0.055);
  const mark = Math.max(5, Math.min(w, h) * 0.24);
  const g = spec.kindDetail.clear().setPosition(spec.x, spec.y);
  g.setAlpha(spec.dimGoldVisual ? 0.44 : 1);
  spec.mysteryIcon.setActive(false).setVisible(false);

  if (spec.kind === "normal") {
    if (spec.isCircuitVisual) {
      g.setActive(false).setVisible(false);
      return;
    }
    const hasSegments = drawBrickCellSegments(g, w, h, spec.widthCells, spec.heightCells);
    g.setActive(hasSegments).setVisible(hasSegments);
    return;
  }

  if (spec.kind === "mystery") {
    if (spec.isCircuitVisual) {
      spec.mysteryIcon.setActive(false).setVisible(false);
      g.setActive(false).setVisible(false);
      return;
    }
    spec.mysteryIcon
      .setTexture(spec.tex.base)
      .setPosition(spec.x, spec.y)
      .setDisplaySize(
        Math.max(20, w * MYSTERY_CHEST_ICON_WIDTH_RATIO),
        Math.max(14, h * MYSTERY_CHEST_ICON_HEIGHT_RATIO),
      )
      .setAlpha(MYSTERY_CHEST_ICON_ALPHA)
      .setActive(true)
      .setVisible(true);
    g.setActive(false).setVisible(false);
    return;
  }

  const detailStroke = spec.kind === "gold" ? PALETTE_NUM.accentAmber : BRICK_DETAIL_COLOR;
  const detailFill = spec.kind === "gold" ? PALETTE_NUM.accentGold : BRICK_DETAIL_COLOR;
  const detailAlpha = spec.kind === "gold" ? GOLD_DETAIL_ALPHA : BRICK_DETAIL_ALPHA;
  const detailFillAlpha = spec.kind === "gold" ? GOLD_DETAIL_FILL_ALPHA : BRICK_DETAIL_FILL_ALPHA;

  if (spec.kind === "gold" && spec.isCircuitVisual) {
    g.setActive(false).setVisible(false);
    return;
  }

  drawBrickCellSegments(g, w, h, spec.widthCells, spec.heightCells);
  g.lineStyle(line, detailStroke, detailAlpha);
  g.fillStyle(detailFill, detailFillAlpha);

  if (spec.kind === "gold") {
    g.fillRoundedRect(-hw + pad * 0.45, -hh + pad * 0.45, w - pad * 0.9, h - pad * 0.9, Math.max(2, line));
    g.fillStyle(PALETTE_NUM.textBright, 0.42);
    g.fillRoundedRect(-hw + pad, -hh + pad * 0.75, w - pad * 2, Math.max(2, line), line);
    g.fillCircle(hw - pad * 1.5, -hh + pad * 1.5, Math.max(2, mark * 0.22));
  } else if (spec.kind === "hard" || spec.kind === "armor") {
    for (let i = -1; i <= 1; i++) {
      const cx = i * mark * 0.72;
      g.beginPath().moveTo(cx - mark * 0.55, hh - pad).lineTo(cx + mark * 0.55, -hh + pad).strokePath();
    }
  } else if (spec.kind === "boss") {
    drawBossCorners(g, hw, hh, pad, Math.max(6, Math.min(w, h) * 0.25));
  } else if (spec.kind === "regen") {
    const cx = -hw + pad + mark * 0.45;
    const cy = -hh + pad + mark * 0.45;
    g.fillCircle(cx, cy, mark * 0.42);
    g.lineStyle(line, detailStroke, 0.72)
      .beginPath()
      .moveTo(cx - mark * 0.25, cy)
      .lineTo(cx + mark * 0.25, cy)
      .moveTo(cx, cy - mark * 0.25)
      .lineTo(cx, cy + mark * 0.25)
      .strokePath();
  } else if (spec.kind === "shield") {
    const bar = Math.max(2, line);
    g.fillRoundedRect(-hw + pad, -hh + pad, bar, h - pad * 2, bar);
    g.fillRoundedRect(hw - pad - bar, -hh + pad, bar, h - pad * 2, bar);
  }

  g.setActive(true).setVisible(true);
}

function drawBossCorners(g: Phaser.GameObjects.Graphics, hw: number, hh: number, pad: number, corner: number): void {
  const left = -hw + pad;
  const right = hw - pad;
  const top = -hh + pad;
  const bottom = hh - pad;
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      const x0 = sx < 0 ? left : right;
      const y0 = sy < 0 ? top : bottom;
      g.beginPath().moveTo(x0, y0 + sy * corner).lineTo(x0, y0).lineTo(x0 + sx * corner, y0).strokePath();
    }
  }
}

function drawBrickCellSegments(
  g: Phaser.GameObjects.Graphics,
  width: number,
  height: number,
  cols: number,
  rows: number,
): boolean {
  if (cols <= 1 && rows <= 1) return false;

  const hw = width / 2;
  const hh = height / 2;
  const pad = Math.max(5, Math.min(width, height) * 0.14);
  const groove = Math.max(1, Math.min(width, height) * 0.045);
  const top = -hh + pad;
  const bottom = hh - pad;
  const left = -hw + pad;
  const right = hw - pad;
  const strokeLine = (x0: number, y0: number, x1: number, y1: number): void => {
    g.lineStyle(groove + 1, BRICK_SEGMENT_SHADOW, BRICK_SEGMENT_SHADOW_ALPHA)
      .beginPath()
      .moveTo(x0, y0)
      .lineTo(x1, y1)
      .strokePath()
      .lineStyle(groove, BRICK_DETAIL_COLOR, BRICK_SEGMENT_HIGHLIGHT_ALPHA)
      .beginPath()
      .moveTo(x0 + 0.5, y0 + 0.5)
      .lineTo(x1 + 0.5, y1 + 0.5)
      .strokePath();
  };

  for (let c = 1; c < cols; c += 1) strokeLine(-hw + (width * c) / cols, top, -hw + (width * c) / cols, bottom);
  for (let r = 1; r < rows; r += 1) strokeLine(left, -hh + (height * r) / rows, right, -hh + (height * r) / rows);
  return true;
}
